import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaTrash } from "react-icons/fa";
import { MdEdit } from "react-icons/md";
import SearchIcon from "@mui/icons-material/Search";
import CircularProgress from "@mui/material/CircularProgress";
import CustomPagination from "../../components/Manual_List/Pagination";
import ItemsPerPageSelect from "../../components/Manual_List/ItemsPerPage";
import { SelectChangeEvent } from "@mui/material/Select";
import { ConfirmationModal } from "../../components/shared/ConfrimationModal";
import { useLocation, useNavigate } from "react-router-dom";
import PackageListHeader from "../../components/Headers/PackageListHeader";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  AppDispatch,
  RootState,
} from "../../components/Manual_List/fetch/store";
import { SubCategory, Language } from "../../Types/types";
import { useLanguage } from "../../components/LanguageContext";
import subCategoryListTranslations from "./subCategoryListTranslations";
import {
  deleteSubCategory,
  fetchSubCategoriesForList,
} from "../../components/Manual_List/fetch/slices";

const SubcategoriesList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const location = useLocation();
  const { language } = useLanguage();
  const t = subCategoryListTranslations[language as Language];
  const { state } = location;
  const { names, id } = state || {};
  const [subcategories, setSubcategories] = useState<SubCategory[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);
  const [subCategoryToDelete, setSubCategoryToDelete] =
    useState<SubCategory | null>(null);

  useEffect(() => {
    if (id) {
      dispatch(
        fetchSubCategoriesForList({ language, categoryId: parseInt(id) })
      )
        .unwrap()
        .then((data) => {
          setSubcategories(data || []);
        });
    }
  }, [dispatch, id, language]);

  const subCategoriesStatus = useSelector(
    (state: RootState) => state.data.subcategories.status
  );

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleItemsPerPageChange = (event: SelectChangeEvent<number>) => {
    setItemsPerPage(event.target.value as number);
    setPage(1);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const openConfirmationModal = (subCategory: SubCategory) => {
    setSubCategoryToDelete(subCategory);
    setIsConfirmationModalOpen(true);
  };

  const handleNavigateToAddSubCategory = () => {
    navigate(`/add-subcategory/${id}`, {
      state: {
        names,
        categoryId: id,
        subcategories,
      },
    });
  };

  const handleNavigateToEditSubCategory = (subCategoryId: number) => {
    navigate(`/edit-subcategory/${subCategoryId}`, {
      state: {
        subCategoryId,
        subcategories,
        categoryId: id,
      },
    });
  };

  const handleNavigateToCategories = () => {
    navigate(`/edit-category/${id}`, {
      state: {
        names,
      },
    });
  };

  const handleDeleteConfirmation = async () => {
    if (subCategoryToDelete) {
      const resultAction = await dispatch(
        deleteSubCategory(subCategoryToDelete.id)
      );
      if (deleteSubCategory.fulfilled.match(resultAction)) {
        dispatch(
          fetchSubCategoriesForList({ language, categoryId: parseInt(id) })
        )
          .unwrap()
          .then((data) => {
            setSubcategories(data || []);
          });
        setIsConfirmationModalOpen(false);
      }
    }
  };

  if (subCategoriesStatus === "loading") {
    return (
      <div className="absolute inset-0 bg-gray-100 bg-opacity-75 flex justify-center items-center z-50">
        <CircularProgress />
      </div>
    );
  }

  const filteredSubCategories =
    subcategories?.filter((subCategory: SubCategory) => {
      const name = subCategory.name;
      return (
        searchTerm === "" ||
        name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }) || [];

  const startIndex = (page - 1) * itemsPerPage;
  const paginatedSubCategories = filteredSubCategories.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const pageCount = Math.ceil(filteredSubCategories.length / itemsPerPage);

  return (
    <div className="min-h-screen w-screen flex flex-col bg-gray-100 dark:bg-darkBackground dark:text-white">
      <PackageListHeader />
      <div className="flex flex-col items-center justify-start flex-grow relative">
        <div className="w-full px-4 sm:px-6 md:px-8 py-6 max-w-9xl flex flex-col">
          <h1 className="text-xl font-semibold mb-1">
            {t.subCategoryListTitle}
          </h1>
          <div
            className={`flex ${
              isSmallScreen || isMediumScreen
                ? "flex-col items-center space-y-2 w-full"
                : "justify-between"
            } items-center mb4 `}
          >
            <div
              className={`flex ${
                isSmallScreen || isMediumScreen
                  ? "flex-col space-y-2 w-full"
                  : "space-x-2"
              }`}
            >
              <button
                type="button"
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded font-medium shadow-sm dark:bg-white dark:text-black"
                onClick={handleNavigateToCategories}
              >
                Categories
              </button>
              <button
                type="button"
                className={`bg-purple text-white ${
                  isSmallScreen || isMediumScreen
                    ? "px-2 py-1 w-36"
                    : "px-4 py-2"
                } rounded font-medium shadow-sm`}
              >
                {t.subcategoriesButton}
              </button>
            </div>
            <div
              className={`flex ${
                isSmallScreen || isMediumScreen
                  ? "flex-col space-y-2 items-center w-full"
                  : "space-x-4 mb-2"
              }`}
            >
              <div className="relative w-64">
                <input
                  type="text"
                  placeholder={t.searchPlaceholder}
                  className="px-3 py-2 border rounded-md w-full pr-10 outline-none "
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                <SearchIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600" />
              </div>
            </div>
          </div>
          <div
            className={`flex ${
              isSmallScreen || isMediumScreen
                ? "justify-center mb-4 w-full"
                : "justify-end mb-4"
            }`}
          >
            <button
              className={`bg-purple text-white ${
                isSmallScreen || isMediumScreen
                  ? "px-2 py-1 w-36 mt-2"
                  : "px-4 py-2 w-48"
              } rounded font-medium shadow-sm`}
              onClick={handleNavigateToAddSubCategory}
            >
              {t.addSubCategoryButton}
            </button>
          </div>
          <div className="overflow-x-auto rounded-md">
            <table className="min-w-full">
              <thead className="bg-gray-100 dark:bg-darkSecondaryBackground">
                <tr>
                  <th className="py-2 px-4 font-medium text-left">{t.name}</th>
                  <th className="py-2 px-4 font-medium text-left"></th>
                </tr>
              </thead>
              <tbody>
                {paginatedSubCategories.map(
                  (subCategory: SubCategory, index: number) => (
                    <tr
                      key={subCategory.id}
                      className={`${
                        index !== paginatedSubCategories.length - 1
                          ? "border-b border-gray-300"
                          : ""
                      } group`}
                    >
                      <td className="py-2 px-4 text-left font-extralight bg-white dark:bg-darkSecondaryBackground">
                        {subCategory.name}
                      </td>

                      <td className="py-2 px-4 text-right flex font-extralight space-x-2 items-center bg-white dark:bg-darkSecondaryBackground">
                        <MdEdit
                          className="text-black ml-auto cursor-pointer dark:text-white"
                          size={24}
                          onClick={() =>
                            handleNavigateToEditSubCategory(subCategory.id)
                          }
                        />

                        <FaTrash
                          className="text-red-500 ml-auto cursor-pointer"
                          size={20}
                          onClick={() => openConfirmationModal(subCategory)}
                        />
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
          {paginatedSubCategories.length === 0 && (
            <div className="text-center my-4 col-span-full">
              <p className="text-lg text-gray-500">
                {" "}
                No data matches this search term.
              </p>
            </div>
          )}
          <div className="flex justify-between items-center pt-6 w-full">
            <CustomPagination
              page={page}
              pageCount={pageCount}
              onPageChange={handlePageChange}
            />
            <ItemsPerPageSelect
              itemsPerPage={itemsPerPage}
              handleItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </div>
      </div>
      {subCategoryToDelete && (
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          onClose={() => setIsConfirmationModalOpen(false)}
          onConfirm={handleDeleteConfirmation}
          label={t.deleteSubCategoryConfirmation.replace(
            "{label}",
            subCategoryToDelete.name
          )}
        />
      )}
    </div>
  );
};

export default SubcategoriesList;
